"use client";

// Formulario colapsable "Anotate en lista de espera", mostrado en el widget público cuando una
// fecha/mes no tiene turnos libres. Ver server/routes/public.ts (POST /api/public/waitlist).
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";

type ServiceOption = { id: string; name: string };

const fieldStyle = css({
  width: "100%",
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  borderColor: "var(--pp-border, #e2e8f0)",
  bg: "#ffffff",
  color: "#0f172a",
  fontSize: "sm",
  _placeholder: { color: "#64748b" },
  _focus: { outline: "none", borderColor: "token(colors.brand.500)", boxShadow: "0 0 0 1px token(colors.brand.500)" },
});

const fieldLabel = css({ fontSize: "xs", fontWeight: "medium" });

export function WaitlistSignupForm({
  slug,
  primaryColor,
  services,
}: {
  slug: string;
  primaryColor: string;
  services: ServiceOption[];
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit() {
    setErr(null);
    if (name.trim().length < 2 || !email.trim() || phone.trim().length < 6) {
      setErr("Completá nombre, email y teléfono.");
      return;
    }
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        slug,
        clientName: name.trim(),
        clientEmail: email.trim(),
        clientPhone: phone.trim(),
      };
      if (serviceId) body.serviceId = serviceId;
      if (dateFrom) body.preferredDateFrom = new Date(`${dateFrom}T00:00:00`).toISOString();
      if (dateTo) body.preferredDateTo = new Date(`${dateTo}T23:59:59`).toISOString();

      const res = await fetch("/api/public/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.status === 409) {
        setErr("Ya estás en lista de espera para este consultorio.");
        return;
      }
      if (!res.ok) {
        setErr(data.error ?? "No se pudo completar la anotación.");
        return;
      }
      setDone(true);
    } catch {
      setErr("Error de conexión. Probá de nuevo.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={css({
        borderWidth: "1px",
        borderColor: "var(--pp-border, #e2e8f0)",
        rounded: "xl",
        overflow: "hidden",
        bg: "var(--pp-card-bg, white)",
      })}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={css({
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "2",
          px: "4",
          py: "3",
          border: "none",
          bg: "transparent",
          cursor: "pointer",
          fontSize: "sm",
          fontWeight: "semibold",
          textAlign: "left",
        })}
        style={{ color: "var(--pp-text, inherit)" }}
      >
        ¿No encontrás horario? Anotate en lista de espera
        <ChevronDown
          size={16}
          aria-hidden
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms cubic-bezier(0.23,1,0.32,1)" }}
        />
      </button>

      {open ? (
        <div
          className={css({
            px: "4",
            pb: "4",
            borderTopWidth: "1px",
            borderColor: "var(--pp-border, #e2e8f0)",
            pt: "4",
            display: "flex",
            flexDir: "column",
            gap: "3",
          })}
        >
          {done ? (
            <p className={css({ fontSize: "sm", color: "#15803d", fontWeight: "medium" })}>
              Te avisamos por WhatsApp cuando se libere un turno.
            </p>
          ) : (
            <>
              {err ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}

              <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <span className={fieldLabel} style={{ color: "var(--pp-muted, #64748b)" }}>
                  Nombre
                </span>
                <Input className={fieldStyle} value={name} onChange={(e) => setName(e.target.value)} />
              </label>

              <div className={css({ display: "grid", gap: "3", gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" } })}>
                <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={fieldLabel} style={{ color: "var(--pp-muted, #64748b)" }}>
                    Email
                  </span>
                  <Input className={fieldStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={fieldLabel} style={{ color: "var(--pp-muted, #64748b)" }}>
                    Teléfono
                  </span>
                  <Input className={fieldStyle} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp" />
                </label>
              </div>

              {services.length > 0 ? (
                <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={fieldLabel} style={{ color: "var(--pp-muted, #64748b)" }}>
                    Servicio (opcional)
                  </span>
                  <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} className={cx(fieldStyle)}>
                    <option value="">Cualquier consulta</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <div className={css({ display: "grid", gap: "3", gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" } })}>
                <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={fieldLabel} style={{ color: "var(--pp-muted, #64748b)" }}>
                    Desde (opcional)
                  </span>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={fieldStyle} />
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={fieldLabel} style={{ color: "var(--pp-muted, #64748b)" }}>
                    Hasta (opcional)
                  </span>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={fieldStyle} />
                </label>
              </div>

              <button
                type="button"
                disabled={busy}
                onClick={() => void submit()}
                className={css({
                  alignSelf: "flex-start",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: "5",
                  py: "2.5",
                  rounded: "md",
                  fontSize: "sm",
                  fontWeight: "semibold",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                  transition: "opacity 0.15s, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                  _hover: { opacity: 0.92 },
                  _active: { transform: "scale(0.97)" },
                  _disabled: { opacity: 0.6, cursor: "not-allowed" },
                })}
                style={{ backgroundColor: primaryColor }}
              >
                {busy ? "Enviando…" : "Anotarme en lista de espera"}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
